import { 
  TrendingDown, 
  BarChart3, 
  Sun, 
  Zap,
  ArrowRight,
  CheckCircle2,
  Calculator,
  PiggyBank,
  Clock,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

export const Economics = ({ onContactClick }) => {
  return (
    <main className="pt-16 lg:pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-white" data-testid="economics-hero">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="text-[#10B981] font-medium text-sm uppercase tracking-wide">Wirtschaftlichkeit</span>
            <h1 className="heading-xl text-[#0F172A] mt-2 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Messbare Rendite durch intelligente Energiespeicherung
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Ein BESS ist keine Ausgabe – es ist eine Investition mit kalkulierbarem Return. 
              Wir zeigen Ihnen transparent, wie sich jeder Euro rechnet.
            </p>
            <Button 
              onClick={onContactClick}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="economics-cta-btn"
            >
              Individuelle Analyse anfragen
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Example Scenarios */}
      <section className="section-padding bg-white" data-testid="example-scenarios">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Beispielszenarien nach Krankenhausgröße
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Realistische Kalkulationen basierend auf typischen Lastprofilen deutscher Krankenhäuser.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Small Hospital */}
            <Card className="border-2 border-slate-100">
              <CardHeader className="bg-slate-50 border-b">
                <CardTitle className="text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <span className="text-sm text-slate-500 block mb-1">Kleines Krankenhaus</span>
                  <span className="text-2xl">100-200 Betten</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Jahresverbrauch</span>
                  <span className="font-medium">2-3 Mio. kWh</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Stromkosten</span>
                  <span className="font-medium">400-600k €</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">BESS-Kapazität</span>
                  <span className="font-medium">500 kWh</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Investition</span>
                  <span className="font-medium">~500k €</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Einsparung/Jahr</span>
                  <span className="font-semibold text-[#10B981]">60-90k €</span>
                </div>
                <div className="flex justify-between py-3 bg-emerald-50 rounded-lg px-3 -mx-3">
                  <span className="text-[#10B981] font-medium">Amortisation</span>
                  <span className="font-bold text-[#10B981]">~6-8 Jahre</span>
                </div>
              </CardContent>
            </Card>

            {/* Medium Hospital - Featured */}
            <Card className="border-2 border-emerald-200 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#10B981] text-white text-xs font-medium px-3 py-1 rounded-full">
                Häufigstes Szenario
              </div>
              <CardHeader className="bg-emerald-50 border-b border-emerald-100">
                <CardTitle className="text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <span className="text-sm text-emerald-600 block mb-1">Mittleres Krankenhaus</span>
                  <span className="text-2xl">300-500 Betten</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Jahresverbrauch</span>
                  <span className="font-medium">5-8 Mio. kWh</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Stromkosten</span>
                  <span className="font-medium">1-1,6 Mio. €</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">BESS-Kapazität</span>
                  <span className="font-medium">1 MWh</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Investition</span>
                  <span className="font-medium">~950k €</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Einsparung/Jahr</span>
                  <span className="font-semibold text-[#10B981]">150-240k €</span>
                </div>
                <div className="flex justify-between py-3 bg-emerald-50 rounded-lg px-3 -mx-3">
                  <span className="text-[#10B981] font-medium">Amortisation</span>
                  <span className="font-bold text-[#10B981]">~5-6 Jahre</span>
                </div>
              </CardContent>
            </Card>

            {/* Large Hospital */}
            <Card className="border-2 border-slate-100">
              <CardHeader className="bg-slate-50 border-b">
                <CardTitle className="text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <span className="text-sm text-slate-500 block mb-1">Großes Klinikum</span>
                  <span className="text-2xl">800+ Betten</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Jahresverbrauch</span>
                  <span className="font-medium">15+ Mio. kWh</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Stromkosten</span>
                  <span className="font-medium">3+ Mio. €</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">BESS-Kapazität</span>
                  <span className="font-medium">2-3 MWh</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Investition</span>
                  <span className="font-medium">~1,8-2,5 Mio. €</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Einsparung/Jahr</span>
                  <span className="font-semibold text-[#10B981]">400-600k €</span>
                </div>
                <div className="flex justify-between py-3 bg-emerald-50 rounded-lg px-3 -mx-3">
                  <span className="text-[#10B981] font-medium">Amortisation</span>
                  <span className="font-bold text-[#10B981]">~4-5 Jahre</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-100">
            <p className="text-blue-800 text-center">
              <strong>Hinweis:</strong> Die tatsächlichen Werte hängen stark von Ihrem individuellen Lastprofil, 
              Tarifstruktur und regionalen Netzentgelten ab. Wir erstellen Ihnen gerne eine detaillierte Analyse.
            </p>
          </div>
        </div>
      </section>

      {/* Financing Options */}
      <section className="section-padding bg-slate-50" data-testid="financing">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Finanzierungsmöglichkeiten
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Flexible Modelle für unterschiedliche Budgetsituationen.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="card-feature">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Direktinvestition
                </h3>
                <p className="text-slate-600 mb-4">
                  Volle Eigentümerschaft mit maximaler Rendite. 
                  Ideal für liquiditätsstarke Träger.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" />
                    <span>100% der Einsparungen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" />
                    <span>AfA-Vorteile nutzbar</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" />
                    <span>Fördermittel anrechenbar</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="card-feature">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Leasing / Mietkauf
                </h3>
                <p className="text-slate-600 mb-4">
                  Liquiditätsschonend mit planbaren Raten. 
                  Einsparungen finanzieren die Rate.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" />
                    <span>Keine Anfangsinvestition</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" />
                    <span>Feste Monatsraten</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" />
                    <span>Option auf Übernahme</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="card-feature">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Contracting
                </h3>
                <p className="text-slate-600 mb-4">
                  Wir investieren, Sie profitieren. 
                  Ohne eigenes Kapital vom ersten Tag an sparen.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" />
                    <span>Kein Kapitalaufwand</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" />
                    <span>Garantierte Einsparung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" />
                    <span>Full-Service inklusive</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0F172A] text-white" data-testid="economics-cta">
        <div className="container-main text-center">
          <h2 className="heading-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Wie hoch ist Ihr Einsparpotenzial?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Wir analysieren Ihr Lastprofil und erstellen eine individuelle Wirtschaftlichkeitsberechnung – 
            kostenlos und unverbindlich.
          </p>
          <Button 
            onClick={onContactClick}
            className="bg-[#10B981] hover:bg-emerald-600 text-white px-8 py-4 rounded-md font-medium inline-flex items-center gap-2"
            data-testid="economics-final-cta"
          >
            Individuelle Analyse anfragen
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>
    </main>
  );
};
